/**
 * FAQ Helper Utilities
 *
 * Utilities for working with FAQ data, including filtering and validation
 */

import { FAQItem } from '../../types/FAQTypes'

/**
 * Check if an answer text is the missing answer placeholder
 *
 * @param answerText - The translated answer text
 * @param missingAnswerText - The missing answer placeholder text
 * @returns True if the answer is missing (matches placeholder)
 */
export function isMissingAnswer(answerText: string, missingAnswerText: string): boolean {
  if (!answerText || !missingAnswerText) return false

  // Check if the answer exactly matches the missing answer text
  return answerText.trim() === missingAnswerText.trim()
}

/**
 * Filter out FAQs that have missing answers
 *
 * @param faqItems - Array of FAQ items
 * @param translateFn - Translation function t() from useTranslation
 * @param missingAnswerKey - Translation key for missing answer text
 * @returns Filtered array with only FAQs that have real answers
 */
export function filterFAQsWithAnswers(
  faqItems: FAQItem[],
  translateFn: (key: string) => string,
  missingAnswerKey: string = 'faq.missingAnswer'
): FAQItem[] {
  const missingAnswerText = translateFn(missingAnswerKey)

  return faqItems.filter((faq) => {
    const answerText = translateFn(faq.descriptionKey)
    return !isMissingAnswer(answerText, missingAnswerText)
  })
}

/**
 * Get all unique categories from FAQ items
 *
 * @param faqItems - Array of FAQ items
 * @returns Array of unique category strings
 */
export function getUniqueFAQCategories(faqItems: FAQItem[]): string[] {
  const categoriesSet = new Set<string>()

  faqItems.forEach((faq) => {
    faq.categories.forEach((category) => {
      categoriesSet.add(category)
    })
  })

  return Array.from(categoriesSet).sort()
}
